import React, { ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormikFormLayout from 'core/page/layout/FormikFormLayout'
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import { SelectChangeEvent } from '@mui/material/Select'
import { ProposalSource } from '@cbgms/api'
import { useEnumAsOptions } from '@cbgms/base/enums/use-enum-as-options'
import { ShopFacilities, ShopPaymentMethods, ShopServices } from '@carsys/enums/shop-facilities'
import { getConceptConfigurationByConcept } from '../state/actions'
import { useDispatch } from 'react-redux'
import { useField } from 'formik'
import { ConfigurationItem, GetList, IPostConceptConfiguration } from '@cbgms/api/support/conceptConfiguration/apis'
import { DBConceptConfigurations } from '@cbgms/api/modules/concept-configuration'
import { useLocation } from 'react-router-dom'
import { Dropdown } from '@carsys/ui'

interface ISelectAllCheckbox {
  isFacilitiesIndeterminate: boolean
  isAllFacilitiesChecked: boolean
  isServicesIndeterminate: boolean
  isAllServicesChecked: boolean
  isPaymentIndeterminate: boolean
  isAllPaymentChecked: boolean
}

interface ConceptConfigurationFormFieldsProps {
  listData: GetList[] | undefined
}

const ConceptConfigurationFormFields: React.FC<ConceptConfigurationFormFieldsProps> = ({ listData }) => {
  const applications = listData?.map(item => item.ConceptConfiguration.Application)

  const { t } = useTranslation()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isNew = location.pathname.includes('/new')
  const [selectAllCheckbox, setSelectAllCheckbox] = React.useState<ISelectAllCheckbox>({
    isFacilitiesIndeterminate: false,
    isAllFacilitiesChecked: false,
    isServicesIndeterminate: false,
    isAllServicesChecked: false,
    isPaymentIndeterminate: false,
    isAllPaymentChecked: false
  })
  const dispatch = useDispatch()
  const [allConceptConfigurations, , allConceptConfigurationsHelpers] = useField<DBConceptConfigurations[]>('AllConceptConfigurations')
  const [deletedConfigurations, , deletedConfigurationsHelpers] = useField<DBConceptConfigurations[]>('DeletedConfigurations')
  const [values, , valuesHelpers] = useField<IPostConceptConfiguration>('Values')
  const [source, setSource] = React.useState(values.value?.Application || '')

  const facilities = useEnumAsOptions(ShopFacilities, 'EnumShopFacilities')
  const services = useEnumAsOptions(ShopServices, 'EnumShopServices')
  const paymentMethods = useEnumAsOptions(ShopPaymentMethods, 'EnumShopPaymentMethods')
  const sources = Object.values(ProposalSource)
  const filteredSources = sources.filter(item => !applications?.includes(item))
  const selectSources = isNew ? filteredSources : sources

  useEffect(() => {
    if (source !== values.value.Application && source !== '') {
      valuesHelpers.setValue({ ...values.value, Application: source })
    }
  }, [source, values.value.Application])

  const onCheck = (e: any, type: string, item: any) => {
    let totalValuesLength = values.value.Configurations
      ? values.value.Configurations.filter(configuration => configuration.Type === type).length
      : 0
    let totalAllConceptsLength = allConceptConfigurations.value.filter(configuration => configuration.Type === type).length
    const existsInAllConfiguration = allConceptConfigurations.value
      .map(configuration => configuration.Type === type && configuration.Value)
      .includes(item.value)
    const existsInDeletedConfiguration = deletedConfigurations.value
      .map(configuration => configuration.Type === type && configuration.Value)
      .includes(item.value)
    const existsInValues =
      values.value?.Configurations &&
      values.value.Configurations.map(configuration => configuration.Type === type && configuration.Values).includes(item.value)
    if (e.target.checked) {
      if (!existsInValues && !existsInAllConfiguration && !existsInDeletedConfiguration) {
        valuesHelpers.setValue({
          ...values.value,
          Configurations: values.value.Configurations
            ? [...values.value.Configurations, { Type: type, Values: item.value }]
            : [{ Type: type, Values: item.value }]
        })
        totalValuesLength += 1
      } else if (existsInDeletedConfiguration && !existsInValues && !existsInAllConfiguration) {
        const itemSelected = deletedConfigurations.value.find(configuration => configuration.Value === item.value)
        if (itemSelected) {
          allConceptConfigurationsHelpers.setValue([...allConceptConfigurations.value, itemSelected])
          deletedConfigurationsHelpers.setValue(deletedConfigurations.value.filter(configuration => configuration.Value !== item.value))
          totalAllConceptsLength += 1
        }
      }
    } else if (existsInValues) {
      const filteredConfigurations = values.value.Configurations.filter(configuration => configuration.Values !== item.value)
      valuesHelpers.setValue({ ...values.value, Configurations: filteredConfigurations })
      totalValuesLength = totalValuesLength !== 0 ? totalValuesLength - 1 : 0
    } else if (existsInAllConfiguration) {
      const filteredConceptConfigurations = allConceptConfigurations.value.filter(configuration => configuration.Value !== item.value)

      const deletedConceptConfigurations = allConceptConfigurations.value.find(configuration => configuration.Value === item.value)

      allConceptConfigurationsHelpers.setValue([...filteredConceptConfigurations])
      totalAllConceptsLength = totalAllConceptsLength !== 0 ? totalAllConceptsLength - 1 : 0

      if (deletedConceptConfigurations) {
        deletedConfigurationsHelpers.setValue([...deletedConfigurations.value, deletedConceptConfigurations])
      }
    }

    const combinedLength = totalValuesLength + totalAllConceptsLength

    switch (type) {
      case 'facilities':
        setSelectAllCheckbox(prev => {
          return {
            ...prev,
            isAllFacilitiesChecked: combinedLength !== 0 && combinedLength === facilities.length,
            isFacilitiesIndeterminate: combinedLength !== 0 && combinedLength < facilities.length
          }
        })
        break
      case 'services':
        setSelectAllCheckbox(prev => {
          return {
            ...prev,
            isAllServicesChecked: combinedLength !== 0 && combinedLength === services.length,
            isServicesIndeterminate: combinedLength !== 0 && combinedLength < services.length
          }
        })
        break
      case 'payment_methods':
        setSelectAllCheckbox(prev => {
          return {
            ...prev,
            isAllPaymentChecked: combinedLength !== 0 && combinedLength === paymentMethods.length,
            isPaymentIndeterminate: combinedLength !== 0 && combinedLength < paymentMethods.length
          }
        })
        break
    }
  }

  const isChecked = (value: string, type: string) => {
    const inAllConcept = allConceptConfigurations.value.find(configuration => configuration.Type === type && configuration.Value === value)
    const inValues =
      values.value.Configurations &&
      values.value.Configurations.find(configuration => configuration.Type === type && configuration.Values === value)
    if (inAllConcept) {
      return true
    }

    if (inValues) {
      return true
    }
    return false
  }

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const selectedValue = event.target.value as string
    setSource(selectedValue)

    try {
      const result = await dispatch(getConceptConfigurationByConcept(selectedValue))
      // eslint-disable-next-line no-console
      console.log('Fetched configuration:', result)
    } catch (error) {
      console.error('Error fetching concept configuration:', error)
    }
  }

  const toggleFacilities = (e: ChangeEvent<HTMLInputElement>, type: string) => {
    let currentType: any = []
    const allCheckbox = {
      ...selectAllCheckbox
    } as ISelectAllCheckbox
    switch (type) {
      case 'facilities':
        allCheckbox.isAllFacilitiesChecked = e.target.checked
        allCheckbox.isFacilitiesIndeterminate = false
        currentType = facilities
        break
      case 'services':
        allCheckbox.isAllServicesChecked = e.target.checked
        allCheckbox.isServicesIndeterminate = false
        currentType = services
        break
      case 'payment_methods':
        allCheckbox.isAllPaymentChecked = e.target.checked
        allCheckbox.isPaymentIndeterminate = false
        currentType = paymentMethods
        break
    }
    setSelectAllCheckbox({ ...allCheckbox })
    if (e.target.checked) {
      const deleted = deletedConfigurations.value.filter(item => item.Type === type)

      const filteredDeleted = deletedConfigurations.value.filter(item => item?.Type !== type)
      deletedConfigurationsHelpers.setValue(filteredDeleted.length > 0 ? [...filteredDeleted] : [])

      if (deleted.length > 0) {
        const filteredSelected = deleted.filter(item => item.Type === type)
        if (filteredSelected.length > 0) {
          allConceptConfigurationsHelpers.setValue([...allConceptConfigurations.value, ...filteredSelected])

          const filteredFacilitiesValues = currentType.reduce((valueArr: ConfigurationItem[], facility: { value: string }) => {
            const facilityFound = filteredSelected.find(item => item.Type === type && item.Value === facility.value)
            if (facilityFound === undefined) {
              valueArr.push({ Type: type, Values: facility.value })
            }
            return valueArr
          }, [])

          valuesHelpers.setValue({
            ...values.value,
            Configurations: values.value?.Configurations
              ? [...values.value.Configurations.filter(item => item?.Type !== type), ...filteredFacilitiesValues]
              : filteredFacilitiesValues
          })
        }
      } else {
        const filterType = currentType.reduce((valueArr: ConfigurationItem[], facility: { value: any }) => {
          const facilityFound = allConceptConfigurations.value.find(item => item.Type && item.Value === facility.value)
          if (facilityFound === undefined) {
            valueArr.push({ Type: type, Values: facility.value })
          }
          return valueArr
        }, [])
        if (filterType.length > 0) {
          const filteredFacilitiesValues = filterType.reduce((valueArr: ConfigurationItem[], facility: { Values: any }) => {
            valueArr.push({ Type: type, Values: facility.Values })
            return valueArr
          }, [])

          valuesHelpers.setValue({
            ...values.value,
            Configurations: values.value?.Configurations
              ? [...values.value.Configurations.filter(item => item?.Type !== type), ...filteredFacilitiesValues]
              : filteredFacilitiesValues
          })
        } else {
          const filteredFacilitiesValues = currentType.reduce((valueArr: ConfigurationItem[], facility: { value: any }) => {
            valueArr.push({ Type: type, Values: facility.value })

            return valueArr
          }, [])

          valuesHelpers.setValue({
            ...values.value,
            Configurations: values.value.Configurations
              ? [...values.value.Configurations, ...filteredFacilitiesValues]
              : filteredFacilitiesValues
          })
        }
      }
    } else {
      const filteredValues = values.value.Configurations && values.value.Configurations.filter(item => item.Type !== type)
      valuesHelpers.setValue({ ...values.value, Configurations: [...filteredValues] })

      const filterDeleted = allConceptConfigurations.value.filter(item => item.Type === type)
      const filterSelected = allConceptConfigurations.value.filter(item => item.Type !== type)
      allConceptConfigurationsHelpers.setValue([...filterSelected])
      deletedConfigurationsHelpers.setValue([...deletedConfigurations.value, ...filterDeleted])
    }
  }

  const getCombinedLength = (type: string) => {
    let combinedLength = 0
    if (allConceptConfigurations.value) {
      const allFacilitiesConceptsLength = allConceptConfigurations.value.filter(item => item.Type === type).length
      combinedLength += allFacilitiesConceptsLength
    }
    if (values.value.Configurations) {
      const allFacilitiesConceptsLength = values.value.Configurations.filter(item => item.Type === type).length
      combinedLength += allFacilitiesConceptsLength
    }

    return combinedLength
  }

  const checkIsAllFacilitiesSelected = () => {
    const combinedFaciltiesLength = getCombinedLength('facilities')

    setSelectAllCheckbox(prev => {
      return {
        ...prev,
        isAllFacilitiesChecked: combinedFaciltiesLength !== 0 && combinedFaciltiesLength === facilities.length,
        isFacilitiesIndeterminate: combinedFaciltiesLength !== 0 && combinedFaciltiesLength < facilities.length
      }
    })
  }

  const checkIsAllServicesSelected = () => {
    const combinedFaciltiesLength = getCombinedLength('services')

    setSelectAllCheckbox(prev => {
      return {
        ...prev,
        isAllServicesChecked: combinedFaciltiesLength !== 0 && combinedFaciltiesLength === services.length,
        isServicesIndeterminate: combinedFaciltiesLength !== 0 && combinedFaciltiesLength < services.length
      }
    })
  }

  const checkIsAllPaymentSelected = () => {
    const combinedFaciltiesLength = getCombinedLength('payment_methods')

    setSelectAllCheckbox(prev => {
      return {
        ...prev,
        isAllPaymentChecked: combinedFaciltiesLength !== 0 && combinedFaciltiesLength === paymentMethods.length,
        isPaymentIndeterminate: combinedFaciltiesLength !== 0 && combinedFaciltiesLength < paymentMethods.length
      }
    })
  }

  useEffect(() => {
    checkIsAllFacilitiesSelected()
    checkIsAllServicesSelected()
    checkIsAllPaymentSelected()
  }, [])

  return (
    <FormikFormLayout
      title={<Typography variant='h5'>{t('Concepts.Settings.ConfigureConcept')}</Typography>}
      backButtonProps={{ to: '/concept-configuration' }}
    >
      <Typography sx={{ my: 4 }}>{t('setting_my_shop.additional_settings.title')}</Typography>
      <FormControl sx={{ width: 500, mb: 2 }}>
        <Dropdown
          name='concept-dropdown'
          disabled={isEdit}
          label={t('Concepts.Settings.Concept')}
          onChange={e => handleChange(e)}
          value={source}
        >
          {selectSources.map(source => {
            return (
              <MenuItem key={source} value={source.toString()}>
                {t(`Enum.EnumProposalSource_${source}`)}
              </MenuItem>
            )
          })}
        </Dropdown>
      </FormControl>

      {source && (
        <Stack direction='row'>
          <Stack flex={1}>
            <Typography variant='h6'>{t('setting_my_shop.additional_settings.facilities_title')}</Typography>
            <Typography variant='body2' sx={{ mb: 2, color: '#0000008A' }}>
              {t('setting_my_shop.additional_settings.facilities_description')}
            </Typography>
            <Stack>
              <FormControlLabel
                key='select-all-facilities'
                control={
                  <Checkbox
                    sx={{ ml: 2 }}
                    name='selectAllFacilities'
                    onChange={e => {
                      toggleFacilities(e, 'facilities')
                    }}
                    indeterminate={selectAllCheckbox.isFacilitiesIndeterminate}
                    checked={selectAllCheckbox.isAllFacilitiesChecked}
                    data-testid='checkbox-select-all-facilities'
                  />
                }
                label={t('global.select_all')}
              />
              {facilities.length > 0 &&
                facilities.map(item => (
                  <FormControlLabel
                    key={`Facilities_${item.value}`}
                    control={
                      <Checkbox
                        sx={{ ml: 4 }}
                        onChange={e => onCheck(e, 'facilities', item)}
                        checked={isChecked(item.value, 'facilities')}
                        data-testid='checkbox-facilities'
                      />
                    }
                    label={item.text}
                  />
                ))}
            </Stack>
          </Stack>
          <Stack flex={1}>
            <Typography variant='h6'>{t('setting_my_shop.additional_settings.services_title')}</Typography>
            <Typography variant='body2' sx={{ mb: 2, color: '#0000008A' }}>
              {t('setting_my_shop.additional_settings.services_description')}
            </Typography>
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ ml: 2 }}
                    onChange={e => {
                      toggleFacilities(e, 'services')
                    }}
                    indeterminate={selectAllCheckbox.isServicesIndeterminate}
                    checked={selectAllCheckbox.isAllServicesChecked}
                    data-testid='checkbox-select-all-services'
                  />
                }
                label={t('global.select_all')}
              />
              {services.length > 0 &&
                services.map(item => (
                  <FormControlLabel
                    key={`Services_${item.value}`}
                    control={
                      <Checkbox
                        sx={{ ml: 4 }}
                        onChange={e => onCheck(e, 'services', item)}
                        checked={isChecked(item.value, 'services')}
                        data-testid='checkbox-services'
                      />
                    }
                    label={item.text}
                  />
                ))}
            </Stack>
          </Stack>
          <Stack flex={1}>
            <Typography variant='h6'>{t('setting_my_shop.additional_settings.payment-method_title')}</Typography>
            <Typography variant='body2' sx={{ mb: 2, color: '#0000008A' }}>
              {t('setting_my_shop.additional_settings.payment_methods_description')}
            </Typography>
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ ml: 2 }}
                    onChange={e => {
                      toggleFacilities(e, 'payment_methods')
                    }}
                    indeterminate={selectAllCheckbox.isPaymentIndeterminate}
                    checked={selectAllCheckbox.isAllPaymentChecked}
                    data-testid='checkbox-select-all-payment-methonds'
                  />
                }
                label={t('global.select_all')}
              />
              {paymentMethods.length > 0 &&
                paymentMethods.map(item => (
                  <FormControlLabel
                    key={`PaymentMethods_${item.value}`}
                    control={
                      <Checkbox
                        sx={{ ml: 4 }}
                        onChange={e => onCheck(e, 'payment_methods', item)}
                        checked={isChecked(item.value, 'payment_methods')}
                        data-testid='checkbox-paymentMethods'
                      />
                    }
                    label={item.text}
                  />
                ))}
            </Stack>
          </Stack>
        </Stack>
      )}
    </FormikFormLayout>
  )
}

export default ConceptConfigurationFormFields
